import React, { useState, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Label } from '../../ui/label';
import { BookOpen, X, Search, Plus, Trash2, Edit3, Check } from 'lucide-react';
import { MealTimeSelector } from '../components/MealTimeSelector';

export function CustomProductsModal({
  isOpen,
  onClose,
  customProducts,
  onAddFood,
  onDeleteProduct,
  onEditProduct,
  onSaveProduct,
  defaultMealTime = 'breakfast',
  mealTimes,
}) {
  const [searchQuery, setSearchQuery] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [selectedMealTime, setSelectedMealTime] = useState(defaultMealTime);
  const [formData, setFormData] = useState({
    name: '',
    calories: '',
    protein: '',
    carbs: '',
    fat: '',
    servingSize: '',
  });

  // Filter products based on search
  const filteredProducts = useMemo(() => {
    if (!searchQuery.trim()) return customProducts;
    const search = searchQuery.toLowerCase();
    return customProducts.filter(p => 
      p.name?.toLowerCase().includes(search)
    );
  }, [customProducts, searchQuery]);

  const resetForm = () => {
    setFormData({
      name: '',
      calories: '',
      protein: '',
      carbs: '',
      fat: '',
      servingSize: '',
    });
    setShowAddForm(false);
    setEditingId(null);
  };

  const handleSave = () => {
    if (!formData.name.trim()) return;
    
    const product = {
      name: formData.name.trim(),
      calories: Number(formData.calories) || 0,
      protein: Number(formData.protein) || 0,
      carbs: Number(formData.carbs) || 0,
      fat: Number(formData.fat) || 0,
      servingSize: Number(formData.servingSize) || 100,
    };
    
    if (editingId) {
      onEditProduct(editingId, product);
    } else {
      onSaveProduct(product);
    }
    resetForm();
  };

  const startEdit = (product) => {
    setFormData({
      name: product.name || '',
      calories: product.calories?.toString() || '',
      protein: product.protein?.toString() || '',
      carbs: product.carbs?.toString() || '',
      fat: product.fat?.toString() || '',
      servingSize: product.servingSize?.toString() || '100',
    });
    setEditingId(product.id);
    setShowAddForm(true);
  };

  const handleClose = () => {
    resetForm();
    setSearchQuery('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-4">
      <Card className="bg-slate-800 border-slate-700 w-full max-w-md max-h-[80vh] overflow-hidden flex flex-col">
        <CardHeader className="flex flex-row items-center justify-between shrink-0">
          <CardTitle className="text-white flex items-center gap-2">
            <BookOpen className="h-5 w-5 text-cyan-400" />
            My Products
          </CardTitle>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleClose}
            className="text-slate-400 hover:text-white"
          >
            <X className="h-5 w-5" />
          </Button>
        </CardHeader>
        
        <CardContent className="overflow-y-auto flex-1 space-y-4">
          {/* Add/Edit Form */}
          {showAddForm ? (
            <div className="space-y-3 p-3 bg-slate-700/50 rounded-lg">
              <div>
                <Label className="text-slate-300 text-sm">Product Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., My Protein Shake"
                  className="bg-slate-700 border-slate-600 text-white mt-1"
                  autoFocus
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label className="text-slate-300 text-xs">Calories</Label>
                  <Input
                    type="number"
                    value={formData.calories}
                    onChange={(e) => setFormData({ ...formData, calories: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Serving (g)</Label>
                  <Input
                    type="number"
                    value={formData.servingSize}
                    onChange={(e) => setFormData({ ...formData, servingSize: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                    placeholder="100"
                  />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-slate-300 text-xs">Protein (g)</Label>
                  <Input
                    type="number"
                    value={formData.protein}
                    onChange={(e) => setFormData({ ...formData, protein: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Carbs (g)</Label>
                  <Input
                    type="number"
                    value={formData.carbs}
                    onChange={(e) => setFormData({ ...formData, carbs: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                    placeholder="0"
                  />
                </div>
                <div>
                  <Label className="text-slate-300 text-xs">Fat (g)</Label>
                  <Input
                    type="number"
                    value={formData.fat}
                    onChange={(e) => setFormData({ ...formData, fat: e.target.value })}
                    className="bg-slate-700 border-slate-600 text-white mt-1"
                    placeholder="0"
                  />
                </div>
              </div>
              <div className="flex gap-2">
                <Button onClick={handleSave} className="flex-1 bg-cyan-600 hover:bg-cyan-500">
                  <Check className="h-4 w-4 mr-1" />
                  {editingId ? 'Update' : 'Save'}
                </Button>
                <Button onClick={resetForm} variant="outline" className="border-slate-600">
                  Cancel
                </Button>
              </div>
            </div>
          ) : (
            <>
              {/* Search & Add Button */}
              <div className="flex gap-2">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search products..."
                    className="pl-10 bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <Button onClick={() => setShowAddForm(true)} className="bg-cyan-600 hover:bg-cyan-500">
                  <Plus className="h-4 w-4" />
                </Button>
              </div>

              {/* Meal Time Selection */}
              <div className="space-y-1">
                <label className="text-sm text-slate-400">Add to:</label>
                <MealTimeSelector 
                  selectedMealTime={selectedMealTime} 
                  onMealTimeChange={setSelectedMealTime}
                  compact
                  mealTimes={mealTimes}
                />
              </div>
              
              {/* Products List */}
              <div className="space-y-2">
                {filteredProducts.length > 0 ? (
                  filteredProducts.map(product => (
                    <div key={product.id} className="p-3 bg-slate-700/50 rounded-lg">
                      <div className="flex items-start justify-between mb-2">
                        <div>
                          <p className="text-white font-medium">{product.name}</p>
                          <p className="text-xs text-slate-500">per {product.servingSize || 100}g</p>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => startEdit(product)}
                            className="h-7 w-7 p-0 text-blue-400 hover:bg-blue-500/20"
                          >
                            <Edit3 className="h-3.5 w-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => onDeleteProduct(product.id)}
                            className="h-7 w-7 p-0 text-red-400 hover:bg-red-500/20"
                          >
                            <Trash2 className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </div>
                      <div className="flex items-center justify-between">
                        <div className="flex gap-2 text-xs">
                          <span className="text-orange-400">{product.calories} cal</span>
                          <span className="text-red-400">P:{product.protein}g</span>
                          <span className="text-yellow-400">C:{product.carbs}g</span>
                          <span className="text-blue-400">F:{product.fat}g</span>
                        </div>
                        <Button
                          size="sm"
                          onClick={() => onAddFood({ ...product, mealTime: selectedMealTime })}
                          className="bg-green-600 hover:bg-green-500 h-7"
                        >
                          <Plus className="h-3.5 w-3.5 mr-1" />
                          Add
                        </Button>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                    <BookOpen className="h-12 w-12 text-slate-600 mx-auto mb-2" />
                    <p className="text-slate-400">
                      {searchQuery ? 'No products found' : 'No custom products yet'}
                    </p>
                    <p className="text-xs text-slate-500 mt-1">
                      Add your own products with custom nutrition info
                    </p>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default CustomProductsModal;
